import { AlertCircle, BookOpen, Check, Copy, ExternalLink, Eye, FileText, PenLine, ShieldCheck } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Panel, PanelHeader } from "../../components/primitives";
import { apiUrl } from "../../lib/env";
import { copyToClipboard, formatDate, getMcpEndpoint } from "../../lib/format";
import type { McpCredential } from "../../lib/types";

/**
 * /help — THE REFERENCE.
 *
 * Two jobs, and the page is composed as three movements that serve them in order:
 *
 *   QUICKSTART  (Connect a client)  the only thing here you DO. Three steps: take the
 *               endpoint, paste the configuration, add the token. It is the only --elev-2
 *               panel with a tinted head, because it is the only panel that is an ACTION.
 *   DOCUMENT    (The MCP reference) this app SHIPS two real reference artefacts —
 *               public/help/mcp.html and public/help/mcp.md. They were represented by two
 *               grey buttons at the bottom of a rail. They are now a typeset sheet you can
 *               read, with the full document one click away.
 *   REFERENCE   (The sequence, and the eleven tools) what you scan.
 *
 * HONESTY, THE THREE PLACES IT BITES ON THIS SCREEN — none of it may be traded for beauty:
 *
 *   THE ENDPOINT is window.location.origin + "/mcp". In local preview that is a dev origin
 *   with no MCP function behind it, and the caption under it says so, verbatim.
 *
 *   THE TOKEN is a READOUT of adc-mcp-credential-v1 and nothing more. "Stored" is the
 *   strongest word available to it: ATLAS has never called the endpoint with this token, so
 *   this row may not say "connected", "verified" or "active". It gets NO tick and NO green —
 *   the stored chip is deliberately NEUTRAL. A green tick here would be the fabricated 42%
 *   wearing a different hat.
 *
 *   THE ELEVEN TOOL DESCRIPTIONS are sourced from netlify/functions/mcp.mjs (the tools[]
 *   array). atlas.approve_run "can send the final copy email and create package metadata" —
 *   an outbound, funder-facing email an autonomous agent can trigger. No stated side effect
 *   may be dropped when condensing one. Diff these strings against mcp.mjs before merge.
 *
 * COLOUR IS RATIONED, AND IT CARRIES MEANING RATHER THAN DECORATING:
 *   accent — the one thing you DO (the Connect panel head, its three step discs).
 *   amber  — the two places this can bite you (a token you have NOT minted; the two workflow
 *            steps whose sentence is a RULE that will stop you).
 * Everything else is --ink / --body / --muted.
 *
 * NOTE FOR CONSOLIDATION: ReferenceTable, EffectTag, CopyButton and CopyField are declared
 * LOCALLY here only because the primitives layer is owned by another workstream during this
 * pass. Their classes are namespaced (.help-*) so promotion is a rename, not a merge.
 */

/* ---------------------------------------------------------------- EFFECT TAG
   A CLOSED union, exhaustively mapped with `satisfies`, so a fourth effect cannot ship
   unmapped. Glyph + WORD + tint, in a FULL ring. Never colour alone, never an edge rail.

   Text on --amber-soft is --amber-deep (4.87:1) and text on --accent-soft is --accent-ink
   (7.06:1) — never --amber (3.44:1) or --accent (4.12:1), both of which fail as text on
   their own soft ground and neither of which a token grep can catch. */

type Effect = "reads" | "writes" | "gated";

const EFFECT_META = {
  reads: { label: "Reads", glyph: Eye },
  writes: { label: "Writes", glyph: PenLine },
  gated: { label: "Gated", glyph: ShieldCheck }
} satisfies Record<Effect, { label: string; glyph: typeof Eye }>;

function EffectTag({ effect }: { effect: Effect }) {
  const meta = EFFECT_META[effect];
  const Glyph = meta.glyph;
  return (
    <span className={`help-effect-tag effect-${effect}`} data-effect={effect}>
      <Glyph aria-hidden="true" />
      {meta.label}
    </span>
  );
}

/* --------------------------------------------------------------- COPY BUTTON
   Confirmation you can SEE at the point of action. The button swaps Copy -> Check and its
   word Copy -> Copied for a beat, while still firing the ONE shared toast — this screen does
   not add an eighth aria-live region. The timer is cleared on unmount, so a copy followed by
   a route change cannot set state on a dead component. */

const COPIED_MS = 1560;

function CopyButton({
  variant,
  testId,
  label,
  onCopy
}: {
  variant: "ghost" | "secondary";
  testId: string;
  label: string;
  onCopy: () => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return (
    <Button
      variant={variant}
      size="sm"
      className="help-copy-btn"
      data-testid={testId}
      aria-label={label}
      onClick={() => {
        void onCopy().then(() => {
          setCopied(true);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), COPIED_MS);
        });
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

/* ---------------------------------------------------------- SYNTAX-COLOURED JSON
   The configuration is the artefact the integrator carries away, so it is TYPESET — not
   regex-highlighted over innerHTML, which is how a highlighter starts editing the thing it
   is supposed to be quoting.

   The rendered tokens and the CLIPBOARD PAYLOAD come from ONE source: the plain text is
   `lines.map(join)`, so the string on the clipboard is byte-for-byte the string on screen,
   <ATLAS_MCP_TOKEN> placeholder and all. Colour cannot leak into the copy, and the copy can
   never drift from the render. A documentation page never puts a live secret in the
   clipboard: Settings is the one place a token is deliberately copied, and this page says so. */

type CodeKind = "key" | "string" | "punct" | "plain";
type CodeToken = { kind: CodeKind; text: string };

function configLines(endpoint: string): CodeToken[][] {
  const key = (text: string): CodeToken => ({ kind: "key", text });
  const str = (text: string): CodeToken => ({ kind: "string", text });
  const pun = (text: string): CodeToken => ({ kind: "punct", text });
  const gap = (text: string): CodeToken => ({ kind: "plain", text });
  return [
    [pun("{")],
    [gap("  "), key('"url"'), pun(": "), str(`"${endpoint}"`), pun(",")],
    [gap("  "), key('"headers"'), pun(": {")],
    [gap("    "), key('"Authorization"'), pun(": "), str('"Bearer <ATLAS_MCP_TOKEN>"')],
    [gap("  "), pun("}")],
    [pun("}")]
  ];
}

function configText(lines: CodeToken[][]): string {
  return lines.map((line) => line.map((token) => token.text).join("")).join("\n");
}

/* ------------------------------------------------------------ REFERENCE TABLE
   A semantic <table>, because eleven tools with three attributes each IS tabular data. It was
   a two-up grid faked with :nth-child(odd) border-right, which left a hanging half-rule
   beside the odd eleventh row.

   PHONE: the <thead> is visually hidden (not display:none — a screen reader still needs the
   column names) and each row is a two-line grid: the mono id with its effect tag on line 1,
   the sentence on line 2.
   >=768: a real table. Eleven ids on one right edge, eleven tags on one stripe, eleven
   sentences on one left edge, one measure. The ids are NOT boxed: eleven mono chips made the
   densest region of the page the noisiest, and the tag already carries the one colour channel
   this region is allowed. */

type ReferenceRow = { id: string; tag: Effect; description: string };

/* THE BREAK OPPORTUNITIES A TYPESETTER WOULD HAVE CHOSEN.

   A mono identifier has no spaces in it, so a line box that cannot fit one has only two ways
   out: overflow the card, or break the token at whatever character the fill happens to reach.
   At 320 the second is what happened — `atlas.get_package_link` came apart as `…lin` / `k`,
   and the reference's own endpoint as `…/mc` / `p`. An orphaned consonant under a URL reads as
   a render fault, and this is a page whose entire job is to be trusted verbatim.

   <wbr> supplies the breaks an author would have picked — after a dot, a slash, an underscore,
   a hyphen — and the CSS layer then forbids every other one (`overflow-wrap: normal`). So the
   string wraps at its own joints or not at all, and it can still never overflow, because every
   segment between two joints is far narrower than the smallest column on the smallest phone.

   <wbr> contributes NO text: the rendered string, textContent, data-tool and the clipboard
   payload are all byte-identical to the constant. */
function MonoText({ text }: { text: string }) {
  const parts: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (".-/_".includes(text[i])) {
      parts.push(text.slice(start, i + 1));
      start = i + 1;
    }
  }
  if (start < text.length) parts.push(text.slice(start));

  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < parts.length - 1 ? <wbr /> : null}
        </Fragment>
      ))}
    </>
  );
}

/* Eleven ids that all begin `atlas.`. The namespace is the same eleven times and carries no
   information, so it RECEDES (--muted) and the method — the only part that differs — sits at
   full --ink. Hierarchy, not colour: the region gains texture without spending a second tint
   in the one place the effect tag already owns the colour channel. The rendered string is
   still byte-identical to the id (`atlas.` + method), and data-tool still carries it whole. */
function ToolId({ id }: { id: string }) {
  const cut = id.indexOf(".");
  if (cut < 0) return <code>{id}</code>;
  return (
    <code>
      <span className="help-ref-ns">{id.slice(0, cut + 1)}</span>
      <wbr />
      <MonoText text={id.slice(cut + 1)} />
    </code>
  );
}

function ReferenceTable({
  caption,
  columns,
  rows,
  testId,
  rowTestId
}: {
  caption: string;
  columns: [string, string, string];
  rows: ReferenceRow[];
  testId: string;
  rowTestId: string;
}) {
  return (
    <table className="help-ref-table" data-testid={testId}>
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{columns[0]}</th>
          <th scope="col">{columns[1]}</th>
          <th scope="col">{columns[2]}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} data-testid={rowTestId} data-tool={row.id} data-effect={row.tag}>
            <td className="help-ref-id">
              <ToolId id={row.id} />
            </td>
            <td className="help-ref-tag">
              <EffectTag effect={row.tag} />
            </td>
            <td className="help-ref-desc">{row.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ==================================================================== CONTENT */

/**
 * The seven steps a run actually follows, each naming the call that performs it. Two
 * disconnected lists (a workflow, and a menu of tools) become ONE reference: an integrator
 * gets the call order the backend itself recommends.
 *
 * `gate: true` marks the two steps whose sentence is not advice but a RULE that will stop
 * you — final QA cannot run while a change is undecided, and approval needs confirm: true and
 * can email the funder. They are the only steps that get a chip, and the rule stays INSIDE
 * its own row, verbatim: a rule that gates an action is not exiled to a "note" card the
 * reader has to correlate by hand.
 */
const WORKFLOW: Array<{ step: string; calls: string[]; gate?: true }> = [
  { step: "Sign in and mint your MCP token in Settings.", calls: [] },
  { step: "Find the partner's official website.", calls: ["atlas.research_website"] },
  { step: "Create the run with the source document and the audience details.", calls: ["atlas.create_run"] },
  { step: "Read the draft, the research ledger and every proposed change.", calls: ["atlas.get_run"] },
  {
    step: "Decide every proposed change: approve, reject, or supply edited text. Final QA cannot run while a change is undecided.",
    calls: ["atlas.review_changes"],
    gate: true
  },
  {
    step: "Approve only after a person has read the reviewed document. Requires confirm: true, runs final QA, and approves only if every gate passes.",
    calls: ["atlas.approve_run"],
    gate: true
  },
  { step: "Read package status, then fetch the download link.", calls: ["atlas.package_status", "atlas.get_package_link"] }
];

/**
 * THE ELEVEN TOOLS. Every description is a faithful condensation of the tool's own
 * description in netlify/functions/mcp.mjs (the tools[] array). This table is documentation
 * of a DEPLOYED backend: if that array changes, this silently lies. Diff these eleven strings
 * against mcp.mjs before merge, and NEVER soften a stated side effect when condensing one —
 * that is precisely the failure this rebuild exists to correct.
 *
 * `tag` is the integrator's governed choice: what an autonomous agent is allowed to call.
 *   reads  — cannot change a run.
 *   writes — changes stored run data (including the human review record).
 *   gated  — runs the backend QA gate, and can approve, package and email.
 */
const TOOLS: ReferenceRow[] = [
  { id: "atlas.get_help", tag: "reads", description: "Connection details, the tool list, and the recommended workflow." },
  { id: "atlas.health", tag: "reads", description: "Checks the MCP endpoint and the ATLAS backend." },
  { id: "atlas.list_runs", tag: "reads", description: "Lists recent runs from the shared Library." },
  {
    id: "atlas.get_run",
    tag: "reads",
    description: "One complete run: input, draft, proposed changes, research, validation and package metadata."
  },
  {
    id: "atlas.research_website",
    tag: "reads",
    description: "Gemini-backed search for an organization's likely official website."
  },
  {
    id: "atlas.create_run",
    tag: "writes",
    description: "Creates a run and returns the draft, the proposed changes and the research ledger."
  },
  {
    id: "atlas.propose_edit",
    tag: "writes",
    description: "Prompts Gemini to edit a run. The result is saved as a proposed change, never applied directly."
  },
  {
    id: "atlas.review_changes",
    tag: "writes",
    description: "Records approve, reject, reset-to-pending, or edited replacement text for each change."
  },
  {
    id: "atlas.approve_run",
    tag: "gated",
    description:
      "Runs final QA and approves only if every backend gate passes. Requires confirm: true. Can send the final-copy email and create the package."
  },
  { id: "atlas.package_status", tag: "reads", description: "QA status, final-copy metadata and package readiness." },
  { id: "atlas.get_package_link", tag: "reads", description: "Returns the package download URL." }
];

/* ------------------------------------------------------------- THE DOCUMENT
   The opening of the reference this app actually ships. Every word below is VERBATIM from
   public/help/mcp.md — sourced from public/help/mcp.md; diff before merge. Nothing is
   invented, nothing is strengthened, and the excerpt visibly CONTINUES (the sheet fades) into
   the full document rather than pretending to be it. */

type DocSpan = { text: string; code?: true };
type DocBlock = { kind: "p" | "h"; spans: DocSpan[] } | { kind: "li"; n: number; spans: DocSpan[] };

export const HELP_DOC_EXCERPT: { title: string; blocks: DocBlock[] } = {
  title: "ATLAS Compiler MCP Help",
  blocks: [
    { kind: "p", spans: [{ text: "Endpoint: " }, { text: "https://atlas-compiler.withmeridian.ai/mcp", code: true }] },
    { kind: "p", spans: [{ text: "Protocol: " }, { text: "2025-11-25", code: true }] },
    { kind: "p", spans: [{ text: "Transport: Streamable HTTP JSON-RPC over POST." }] },
    {
      kind: "p",
      spans: [{ text: "Authentication: " }, { text: "Authorization: Bearer <ATLAS_MCP_TOKEN>", code: true }, { text: "." }]
    },
    { kind: "p", spans: [{ text: "Users get MCP credentials inside the ATLAS app:" }] },
    {
      kind: "li",
      n: 1,
      spans: [
        { text: "Sign in at " },
        { text: "https://atlas-compiler.withmeridian.ai", code: true },
        { text: " with a " },
        { text: "withmeridian.ai", code: true },
        { text: " Google account." }
      ]
    },
    { kind: "li", n: 2, spans: [{ text: "Open Settings." }] },
    { kind: "li", n: 3, spans: [{ text: "In MCP access, copy the endpoint." }] },
    { kind: "li", n: 4, spans: [{ text: "Copy the personal MCP token." }] },
    { kind: "li", n: 5, spans: [{ text: "Paste both into the MCP-compatible AI client." }] },
    { kind: "h", spans: [{ text: "Recommended Workflow" }] },
    {
      kind: "li",
      n: 1,
      spans: [
        { text: "Call " },
        { text: "atlas.get_help", code: true },
        { text: " if the client needs connection details or a safe sequence." }
      ]
    },
    {
      kind: "li",
      n: 2,
      spans: [
        { text: "Call " },
        { text: "atlas.research_website", code: true },
        { text: " when the official partner website is unknown." }
      ]
    }
  ]
};

function DocSpans({ spans }: { spans: DocSpan[] }) {
  return (
    <>
      {spans.map((span, index) =>
        span.code ? (
          <code key={index} className="help-doc-code">
            <MonoText text={span.text} />
          </code>
        ) : (
          <span key={index}>{span.text}</span>
        )
      )}
    </>
  );
}

/* ==================================================================== SCREEN */

export function HelpPage({
  mcpCredential,
  setToast
}: {
  mcpCredential: McpCredential | null;
  setToast: (text: string) => void;
}) {
  const mcpEndpoint = getMcpEndpoint();
  const tokenMinted = Boolean(mcpCredential?.token);
  const localPreview = !apiUrl;

  const lines = configLines(mcpEndpoint);
  const configuration = configText(lines);

  /* Derived from the row data, never typed by hand — a count that can drift out of step with
     the thing it counts is a small lie waiting to happen. */
  const writeCount = TOOLS.filter((tool) => tool.tag === "writes").length;
  const gatedCount = TOOLS.filter((tool) => tool.tag === "gated").length;

  async function copyValue(value: string, confirmation: string) {
    try {
      await copyToClipboard(value);
      setToast(confirmation);
    } catch {
      setToast("Copy failed — select the text and copy it manually");
      throw new Error("copy failed");
    }
  }

  return (
    <div className="help-layout" data-testid="help-page">
      {/* ---------------------------------------------------------------- MASTHEAD
          Sentence case, not uppercase: this codebase reserves uppercase for a data table's
          <th> and for nothing else, app-wide. The counts are chips, and they are DERIVED. */}
      <div className="help-masthead" data-testid="help-masthead">
        <p className="help-eyebrow">
          <BookOpen aria-hidden="true" />
          MCP reference
        </p>
        <h2 className="help-masthead-title">Connect a client to ATLAS</h2>
        <p className="help-lead">
          ATLAS exposes this workflow as an MCP endpoint. A client can create runs, propose edits and record review
          decisions — it cannot skip the QA gate.
        </p>
        {/* The counts are DERIVED from TOOLS[], and they wear the same three glyphs the table's
            effect tags wear — so the masthead is a legend for the reference below it, not a
            decoration. A typed count is a small lie waiting to happen. */}
        <div className="help-masthead-meta">
          <span className="help-count-chip">
            <Eye aria-hidden="true" />
            {TOOLS.length} tools
          </span>
          <span className="help-count-chip">
            <PenLine aria-hidden="true" />
            {writeCount} write
          </span>
          <span className="help-count-chip">
            <ShieldCheck aria-hidden="true" />
            {gatedCount} runs the QA gate
          </span>
          {localPreview ? (
            <span className="help-count-chip is-preview">
              <AlertCircle aria-hidden="true" />
              Local preview
            </span>
          ) : null}
        </div>
      </div>

      {/* ------------------------------------------------------------------ 1. DO
          The quickstart. The only --elev-2 panel on the page, and the only tinted head,
          because it is the only thing here you DO. */}
      <Panel className="help-connect" data-testid="help-connect">
        <PanelHeader title="Connect a client" meta="Three steps. Any MCP-compatible client." />

        {/* THE ORDER IS THE ORDER YOU DO IT IN: take the endpoint, mint the token, and only
            then paste a configuration that needs BOTH. It was endpoint -> config -> token,
            which told you to paste a config before you held the token it carries, and which at
            >=1024 numbered 1 and 3 down the left column with 2 alone across a hairline — a
            numbered sequence that had to be read in a Z.

            The three steps are flat siblings of ONE grid — so every disc, every title and every
            control lands on the same two columns — and at >=1024 grid PLACEMENT alone lifts the
            config into the right half as step 3, top to bottom, no reflow of the DOM. Phone
            order, reading order and tab order are therefore the same order at every width. */}
        <div className="help-steps">
          <div className="help-step help-step--endpoint">
            <span className="help-step-n" aria-hidden="true">
              1
            </span>
            <div className="help-step-content">
              <h3 className="help-step-title">Copy the endpoint</h3>
              <div className="help-copy-field" data-testid="help-endpoint">
                <code className="help-field-value">{mcpEndpoint}</code>
                <CopyButton
                  variant="secondary"
                  testId="help-endpoint-copy"
                  label="Copy endpoint"
                  onCopy={() => copyValue(mcpEndpoint, "Endpoint copied")}
                />
                {localPreview ? (
                  <p className="help-field-caption">
                    Local preview: no backend is reachable here, so this address will not answer MCP calls.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* The token state is a READOUT of local storage. "Stored" is neutral — no tick, no
              green — because ATLAS has never called the endpoint with this token. */}
          <div className="help-step help-step--token">
            <span className="help-step-n" aria-hidden="true">
              2
            </span>
            <div className="help-step-content">
              <h3 className="help-step-title">Add your token</h3>
              <div className="help-token" data-testid="help-token" data-state={tokenMinted ? "minted" : "none"}>
                {tokenMinted ? (
                  <span className="help-token-chip is-stored">Stored in this browser</span>
                ) : (
                  <span className="help-token-chip is-none">
                    <AlertCircle aria-hidden="true" />
                    Not minted
                  </span>
                )}
                <p className="help-token-prose">
                  {tokenMinted ? `Expires ${formatDate(mcpCredential?.expiresAt || "")}` : "No token in this browser."}
                </p>
                <Link className="btn btn-secondary btn-sm" to="/settings" data-testid="help-token-settings">
                  {tokenMinted ? "Manage in Settings" : "Mint a token in Settings"}
                </Link>
                <p className="help-field-caption">
                  Tokens are minted and copied in Settings. ATLAS has not checked this one against the endpoint.
                </p>
              </div>
            </div>
          </div>

          {/* LAST, because it needs both of the above. The configuration is the artefact you
              carry away, so it is typeset: a filename strip, three syntax roles, and its OWN
              horizontal scroll — the JSON never breaks mid-token, and the body never scrolls
              sideways. The scroll is made VISIBLE (a persistent thin bar, and a fade the line
              runs INTO) because a line that is merely clipped at a hard edge reads as broken
              text rather than as a scroller — and the line hidden behind it is the Bearer
              header, the one string an integrator has to check. */}
          <div className="help-step help-step--config">
            <span className="help-step-n" aria-hidden="true">
              3
            </span>
            <div className="help-step-content">
              <h3 className="help-step-title">Paste the configuration</h3>
              <div className="help-code-card">
                <div className="help-code-head">
                  <span className="help-code-name">mcp.json</span>
                  <CopyButton
                    variant="ghost"
                    testId="help-config-copy"
                    label="Copy configuration"
                    onCopy={() => copyValue(configuration, "Configuration copied")}
                  />
                </div>
                <div className="help-code-scroll">
                  <pre className="help-config" data-testid="help-config">
                    <code>
                      {/* The newline is a SIBLING of the line, never a child of it: a line box
                          that contains its own break cannot be measured, and this block's whole
                          promise is that a line is a line. */}
                      {lines.map((line, lineIndex) => (
                        <Fragment key={lineIndex}>
                          <span className="help-code-line">
                            {line.map((token, tokenIndex) => (
                              <span className={`help-code-${token.kind}`} key={tokenIndex}>
                                {token.text}
                              </span>
                            ))}
                          </span>
                          {lineIndex < lines.length - 1 ? "\n" : null}
                        </Fragment>
                      ))}
                    </code>
                  </pre>
                  {/* The fade is a SIBLING of the scroller, so it stays pinned to the card's right
                      edge while the code moves under it. The code carries a --fade-w end padding,
                      so at maximum scroll the last glyph of the Bearer line comes to rest at the
                      START of the gradient, in the clear: the fade can say "this runs on" without
                      ever being able to hide the thing it is pointing at. Over a short line it is
                      code-bg on code-bg, and disappears. */}
                  <span className="help-code-fade" aria-hidden="true" />
                </div>
              </div>
              <p className="help-field-caption">Replace &lt;ATLAS_MCP_TOKEN&gt; with the token from Settings.</p>
            </div>
          </div>
        </div>
      </Panel>

      {/* ------------------------------------------------------------- 2. READ
          The document. This app ships a real reference; render it as one. */}
      <Panel className="help-doc" data-testid="help-doc">
        <PanelHeader title="The MCP reference" meta="The document behind this page." />
        <div className="help-doc-body">
          {/* The stack is a SIBLING of the sheet, not a child of it: painted first, so the sheet
              lands on top of it with no z-index games and no chance of the layers disappearing
              behind the sheet's own background. Decorative, aria-hidden, zero layout cost. */}
          <div className="help-doc-sheet-wrap">
            <span className="help-doc-stack" aria-hidden="true" />
            <div className="help-doc-sheet">
              <div className="help-doc-text">
                <h3 className="help-doc-title">{HELP_DOC_EXCERPT.title}</h3>
                {HELP_DOC_EXCERPT.blocks.map((block, index) =>
                  block.kind === "h" ? (
                    <h4 className="help-doc-heading" key={index}>
                      <DocSpans spans={block.spans} />
                    </h4>
                  ) : block.kind === "li" ? (
                    <p className="help-doc-li" key={index}>
                      <span className="help-doc-n" aria-hidden="true">
                        {block.n}.
                      </span>
                      <span>
                        <DocSpans spans={block.spans} />
                      </span>
                    </p>
                  ) : (
                    <p className="help-doc-p" key={index}>
                      <DocSpans spans={block.spans} />
                    </p>
                  )
                )}
              </div>
            </div>
          </div>

          <div className="help-doc-actions">
            <a
              className="btn btn-secondary btn-md"
              href="/help/mcp.html"
              target="_blank"
              rel="noreferrer"
              data-testid="help-docs-html"
              aria-label="Full reference (opens in a new tab)"
            >
              <ExternalLink aria-hidden="true" />
              Open full reference
            </a>
            {/* The secondary action carries a glyph too. Not decoration: without one, a
                borderless label sits a glyph-plus-gap further in than the bordered button's
                icon above it, and the two actions read as being on two different left edges
                when they stack. Same structure, same box, one edge. */}
            <a
              className="btn btn-ghost btn-md"
              href="/help/mcp.md"
              target="_blank"
              rel="noreferrer"
              data-testid="help-docs-md"
              aria-label="Markdown (opens in a new tab)"
            >
              <FileText aria-hidden="true" />
              Markdown
            </a>
          </div>
        </div>
      </Panel>

      {/* ------------------------------------------------------------ 3. SCAN */}
      <Panel className="help-workflow-panel">
        <PanelHeader title="The sequence a run follows" meta="The order the backend recommends." />
        <ol className="help-workflow" data-testid="help-workflow">
          {WORKFLOW.map((item, index) => (
            <li key={item.step} data-testid="help-workflow-step" data-step={index + 1} data-gate={item.gate ? "true" : "false"}>
              <span className="help-step-number" aria-hidden="true">
                {index + 1}
              </span>
              <div className="help-step-body">
                {item.gate ? (
                  <span className="help-gate-chip">
                    <ShieldCheck aria-hidden="true" />
                    Gate rule
                  </span>
                ) : null}
                <p>{item.step}</p>
                {item.calls.length ? (
                  <p className="help-step-calls">
                    {item.calls.map((call) => (
                      <code key={call}>{call}</code>
                    ))}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </Panel>

      <Panel className="help-tools-panel">
        <PanelHeader title="MCP tools" meta="Every call the endpoint exposes." />
        <div className="help-tools-body">
          <p className="help-legend">
            Reads never change a run. Writes change stored run data. One tool runs the QA gate and can approve, package
            and email.
          </p>
          <div className="help-legend-keys" aria-hidden="true">
            <EffectTag effect="reads" />
            <EffectTag effect="writes" />
            <EffectTag effect="gated" />
          </div>
          <ReferenceTable
            caption="MCP tools, their effect on a run, and what each one does"
            columns={["Tool", "Effect", "What it does"]}
            rows={TOOLS}
            testId="help-tools"
            rowTestId="help-tool-row"
          />
        </div>
      </Panel>
    </div>
  );
}
